import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../../../context/AuthContext';
import ToolIcon from '../../../../shared/ToolIcon/ToolIcon';
import { getToolLabel } from '../../../../shared/toolLabels';
import './Sidebar.css';

/**
 * Sidebar Component
 * 
 * Implements a persistent, multi-layered navigation bar.
 * Features:
 * - Hover-expansion (overlay mode)
 * - Sticky-locking (layout-push mode)
 * - Accordion-style grouped navigation
 * - Apple-inspired light aesthetics
 * 
 * @component
 * @param {Object} props
 * @param {boolean} props.isExpanded - Whether the sidebar is currently hovered or locked.
 * @param {boolean} props.isLocked - Whether the sidebar is pinned (sticky).
 * @param {Function} props.onToggle - Handler to expand the sidebar.
 * @param {Function} props.onToggleLock - Handler to toggle the sticky state.
 * @param {Function} props.onClose - Handler to collapse the sidebar.
 * @param {Object} props.user - The current authenticated user for role-based filtering.
 */
const Sidebar = ({ isExpanded, isLocked, onToggle, onToggleLock, onClose, user }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { toolPermissions } = useAuth();
    
    // Accordion state (persists as long as MainLayout is mounted)
    const [expandedSections, setExpandedSections] = useState({
        tools: true,
        verwaltung: true,
        admin: true
    });

    const toggleSection = (id) => {
        setExpandedSections(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };
    
    // Navigation Groups Definition
    const groups = [
        {
            title: 'Werkzeuge',
            id: 'tools',
            overviewPath: '/tools',
            icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a2 2 0 0 1 2.83 2.83l-3.77 3.77a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a2 2 0 0 1 2.83 2.83l-3.77 3.77" />
                    <path d="M20 16a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z" />
                    <path d="M9 7.1L4 12.1" />
                    <path d="M11 5.1L6 10.1" />
                    <path d="M13 3.1L8 8.1" />
                </svg>
            ),
            items: [
                { path: '/tools/leader', label: getToolLabel('leader'), icon: 'leader', toolKey: 'leader' },
                { path: '/tools/timesheet', label: getToolLabel('timesheet'), icon: 'timesheet', toolKey: 'timesheet' },
                { path: '/tools/journal', label: getToolLabel('journal'), icon: 'journal', toolKey: 'journal' },
                { path: '/tools/rental', label: getToolLabel('rental'), icon: 'rental', toolKey: 'rental' },
                { path: '/tools/files', label: getToolLabel('files'), icon: 'files', toolKey: 'files' },
                { path: '/tools/photos', label: getToolLabel('photos'), icon: 'photos', toolKey: 'photos' },
                { path: '/tools/internalHandover', label: getToolLabel('internalHandover'), icon: 'internalhandover', toolKey: 'internalHandover' },
            ]
        },
        {
            title: 'Verwaltung',
            id: 'verwaltung',
            overviewPath: '/verwaltung',
            icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="16" rx="2" />
                    <path d="M3 10h18" />
                    <path d="M9 20V10" />
                </svg>
            ),
            items: [
                { path: '/verwaltung/mitarbeiter', label: getToolLabel('employee'), icon: 'mitarbeiter', toolKey: 'employee' },
                { path: '/verwaltung/fuhrpark', label: getToolLabel('vehicleStatus'), icon: 'fuhrpark', toolKey: 'vehicleStatus' },
                { path: '/verwaltung/projekte', label: 'Projekte', icon: 'projects' },
                { path: '/verwaltung/kunden', label: 'Kunden', icon: 'customers' },
            ]
        },
        {
            title: 'Admin-Panel',
            id: 'admin',
            overviewPath: '/admin/users',
            role: 'admin',
            icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                    <circle cx="12" cy="12" r="3" />
                </svg>
            ),
            items: [
                { path: '/admin/tools', label: 'Toolverwaltung', icon: 'tools' },
            ]
        }
    ];

    return (
        <aside 
            className={`app-sidebar ${isLocked ? 'locked' : isExpanded ? 'expanded' : 'collapsed'}`}
            onMouseEnter={() => !isLocked && onToggle()}
            onMouseLeave={() => !isLocked && onClose()}
        >
            <div className="sidebar-header">
                <NavLink to="/" className="sidebar-logo">
                    <div className="logo-icon">OX</div>
                    <span className="logo-text">Optimox</span>
                </NavLink>
                {(isExpanded || isLocked) && (
                    <button 
                        className={`sidebar-toggle-btn ${isLocked ? 'active' : ''}`}
                        onClick={onToggleLock}
                        aria-label={isLocked ? "Leiste einklappen" : "Leiste fixieren"}
                    >
                        {isLocked ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15 18l-6-6 6-6"/>
                            </svg>
                        ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 18l6-6-6-6"/>
                            </svg>
                        )}
                    </button>
                )}
            </div>

            <nav className="sidebar-nav">
                {/* Global Menu Link at the Top */}
                <NavLink 
                    to="/admin" 
                    end 
                    className={({ isActive }) => isActive ? 'nav-item main-menu-item active' : 'nav-item main-menu-item'}
                >
                    <span className="item-icon"><ToolIcon name="home" mode="emoji" /></span>
                    <span className="item-label">Hauptmenü</span>
                </NavLink>

                <div className="nav-divider" />

                {groups.map((group) => {
                    if (group.role && user?.role !== group.role) return null;
                    const isExpandedSection = expandedSections[group.id];
                    const isGroupActive = location.pathname.startsWith(group.overviewPath) || 
                                          group.items.some(item => location.pathname.startsWith(item.path));

                    return (
                        <div key={group.id} className={`nav-group ${isExpandedSection ? 'is-open' : 'is-closed'} ${isGroupActive ? 'active' : ''}`}>
                            <div className="group-header">
                                <NavLink 
                                    to={group.overviewPath} 
                                    className={({ isActive }) => (isActive || isGroupActive) ? 'group-link active' : 'group-link'}
                                    onClick={(e) => {
                                        if (location.pathname === group.overviewPath) {
                                            e.preventDefault();
                                            toggleSection(group.id);
                                        }
                                    }}
                                >
                                    <span className="group-icon">{group.icon}</span>
                                    <span className="group-title">{group.title}</span>
                                </NavLink>
                                <button className="group-toggle" onClick={() => toggleSection(group.id)}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M6 9l6 6 6-6"/>
                                    </svg>
                                </button>
                            </div>
                            <div className="group-items">
                                {group.items.filter(item => {
                                    if (item.path === '/verwaltung/projekte') {
                                        const journalTool = toolPermissions.journal;
                                        // Some structures have it flattened, some have it in .permissions
                                        const projectPerms = journalTool?.permissions || journalTool; 
                                        const isGlobalAdmin = user?.role === 'admin';
                                        const hasRights = isGlobalAdmin || projectPerms?.view_all_projects || projectPerms?.create_projects;
                                        
                                        
                                        return hasRights;
                                    }
                                    if (item.toolKey) {
                                        const tool = toolPermissions[item.toolKey];
                                        return tool && tool.granted && tool.enabled;
                                    }
                                    return true;
                                }).map((item) => (
                                    <NavLink 
                                        key={item.path} 
                                        to={item.path} 
                                        end={item.end}
                                        className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}
                                    >
                                        <span className="item-icon"><ToolIcon name={item.icon} mode="emoji" /></span>
                                        <span className="item-label">{item.label}</span>
                                    </NavLink>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </nav>

            <div className="sidebar-footer">
                <div className="status-indicator">
                    <span className="status-dot"></span>
                    <span className="status-label">Online</span>
                </div>
            </div>
        </aside>
    );
};

export default Sidebar;
